import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Package, ShoppingCart, TrendingUp, Truck, Clock, Trash2, Wheat, Pencil, CheckCircle, Scale, Eye, X, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRAIN_COLORS = {
  MAIZ: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', icon: 'bg-yellow-500' },
  TRIGO: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: 'bg-amber-500' },
  OTROS: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-800', icon: 'bg-slate-500' },
};

const formatKg = (kg) => {
  if (!kg && kg !== 0) return '-';
  const ton = kg / 1000;
  return `${ton.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ton`;
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const Inventario = () => {
  const { user: currentUser } = useAuth();
  const [resumen, setResumen] = useState([]);
  const [totales, setTotales] = useState({});
  const [compras, setCompras] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [importadoras, setImportadoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCompraModalOpen, setIsCompraModalOpen] = useState(false);
  const [isEditCompraModalOpen, setIsEditCompraModalOpen] = useState(false);
  const [editCompraId, setEditCompraId] = useState(null);
  const [editCompraForm, setEditCompraForm] = useState({ cantidad_kg: '', fecha_compra: '', precio_unitario_usd: '', observaciones: '' });
  const [compraForm, setCompraForm] = useState({
    proveedor_id: '', tipo_grano: '', contrato: '', cantidad_kg: '',
    fecha_compra: '', importadora_id: '', precio_unitario_usd: '', observaciones: ''
  });
  
  // Modal para ajuste de balanza
  const [isAjusteModalOpen, setIsAjusteModalOpen] = useState(false);
  const [ajusteCompra, setAjusteCompra] = useState(null);
  const [ajusteKg, setAjusteKg] = useState('');

  // Modal para rentabilidad
  const [isRentabilidadModalOpen, setIsRentabilidadModalOpen] = useState(false);
  const [rentabilidadData, setRentabilidadData] = useState(null);
  const [rentabilidadLoading, setRentabilidadLoading] = useState(false);
  const [rentabilidadForm, setRentabilidadForm] = useState({
    costo_aduanero_usd_ton: '',
    costo_admin_usd_ton: '',
    otros_costos_usd: ''
  });

  // Para subir documento del contrato
  const fileInputRef = React.useRef(null);
  const [pendingContratoCompraId, setPendingContratoCompraId] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [resumenRes, comprasRes, provRes, impRes] = await Promise.all([
        axios.get(`${API}/inventario/resumen`),
        axios.get(`${API}/compras`),
        axios.get(`${API}/proveedores`),
        axios.get(`${API}/importadoras`)
      ]);
      setResumen(resumenRes.data.resumen);
      setTotales(resumenRes.data.totales);
      setCompras(comprasRes.data);
      setProveedores(provRes.data);
      setImportadoras(impRes.data);
    } catch {
      toast.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetCompraForm = () => {
    setCompraForm({
      proveedor_id: '', tipo_grano: '', contrato: '', cantidad_kg: '',
      fecha_compra: '', importadora_id: '', precio_unitario_usd: '', observaciones: ''
    });
  };

  const handleCreateCompra = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/compras`, {
        ...compraForm,
        cantidad_kg: parseFloat(compraForm.cantidad_kg) * 1000,  // usuario ingresa toneladas, backend espera KG
        precio_unitario_usd: parseFloat(compraForm.precio_unitario_usd)
      });
      toast.success('Compra registrada exitosamente');
      setIsCompraModalOpen(false);
      resetCompraForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar compra');
    }
  };

  const handleDeleteCompra = async (id) => {
    if (!window.confirm('¿Eliminar esta compra?')) return;
    try {
      await axios.delete(`${API}/compras/${id}`);
      toast.success('Compra eliminada');
      fetchAll();
    } catch { toast.error('Error al eliminar compra'); }
  };

  // Abrir modal de ajuste de balanza
  const openAjusteModal = (compra) => {
    setAjusteCompra(compra);
    setAjusteKg(((compra.ajuste_kg || 0) / 1000).toString());
    setIsAjusteModalOpen(true);
  };

  // Guardar ajuste de balanza
  const handleGuardarAjuste = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/compras/${ajusteCompra.id}`, {
        ajuste_kg: parseFloat(ajusteKg || 0) * 1000
      });
      toast.success('Ajuste guardado');
      setIsAjusteModalOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar ajuste');
    }
  };

  // Cerrar/Completar compra
  const handleCerrarCompra = async (compra) => {
    const accion = compra.cerrada ? 'reabrir' : 'cerrar';
    if (!window.confirm(`¿Desea ${accion} esta compra? ${!compra.cerrada ? 'El disponible se pondrá en 0.' : 'Se recalculará el disponible.'}`)) return;
    try {
      await axios.put(`${API}/compras/${compra.id}`, {
        cerrada: !compra.cerrada
      });
      toast.success(`Compra ${compra.cerrada ? 'reabierta' : 'cerrada'}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar compra');
    }
  };

  // Abrir modal de rentabilidad
  const openRentabilidadModal = async (compra) => {
    setRentabilidadLoading(true);
    setIsRentabilidadModalOpen(true);
    try {
      const res = await axios.get(`${API}/compras/${compra.id}/rentabilidad`);
      setRentabilidadData(res.data);
      setRentabilidadForm({
        costo_aduanero_usd_ton: res.data.costo_aduanero_usd_ton || '',
        costo_admin_usd_ton: res.data.costo_admin_usd_ton || '',
        otros_costos_usd: res.data.otros_costos_usd || ''
      });
    } catch (err) {
      toast.error('Error al cargar datos de rentabilidad');
      setIsRentabilidadModalOpen(false);
    } finally {
      setRentabilidadLoading(false);
    }
  };

  // Guardar rentabilidad
  const handleGuardarRentabilidad = async (e) => {
    e.preventDefault();
    if (!rentabilidadData) return;
    try {
      const res = await axios.put(`${API}/compras/${rentabilidadData.compra_id}/rentabilidad`, {
        costo_aduanero_usd_ton: parseFloat(rentabilidadForm.costo_aduanero_usd_ton) || 0,
        costo_admin_usd_ton: parseFloat(rentabilidadForm.costo_admin_usd_ton) || 0,
        otros_costos_usd: parseFloat(rentabilidadForm.otros_costos_usd) || 0
      });
      setRentabilidadData(res.data);
      toast.success('Rentabilidad actualizada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar rentabilidad');
    }
  };

  // Funciones para documento del contrato
  const handleUploadContratoClick = (compraId) => {
    setPendingContratoCompraId(compraId);
    fileInputRef.current?.click();
  };

  const handleContratoFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (file && pendingContratoCompraId) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/compras/${pendingContratoCompraId}/contrato`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Documento subido correctamente');
        fetchAll();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error al subir documento');
      }
    }
    e.target.value = '';
    setPendingContratoCompraId(null);
  };

  const viewContrato = (contratoUrl) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}/api${contratoUrl}`;
    window.open(fullUrl, '_blank');
  };

  const deleteContrato = async (compraId) => {
    if (!window.confirm('¿Eliminar el documento del contrato?')) return;
    try {
      await axios.delete(`${API}/compras/${compraId}/contrato`);
      toast.success('Documento eliminado');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar documento');
    }
  };

  const openEditCompraModal = (c) => {
    setEditCompraId(c.id);
    setEditCompraForm({
      cantidad_kg: (c.cantidad_kg / 1000).toString(),
      fecha_compra: c.fecha_compra,
      precio_unitario_usd: c.precio_unitario_usd.toString(),
      observaciones: c.observaciones || ''
    });
    setIsEditCompraModalOpen(true);
  };

  const handleEditCompra = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/compras/${editCompraId}`, {
        cantidad_kg: parseFloat(editCompraForm.cantidad_kg) * 1000,
        fecha_compra: editCompraForm.fecha_compra,
        precio_unitario_usd: parseFloat(editCompraForm.precio_unitario_usd),
        observaciones: editCompraForm.observaciones
      });
      toast.success('Compra actualizada');
      setIsEditCompraModalOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar compra');
    }
  };

  const setField = (field, val) => setCompraForm(prev => ({ ...prev, [field]: val }));

  const summaryCards = [
    { key: 'comprado_kg', label: 'Total Comprado', icon: ShoppingCart, color: 'bg-blue-500' },
    { key: 'vendido_kg', label: 'Total Vendido', icon: TrendingUp, color: 'bg-emerald-500' },
    { key: 'disponible_venta_kg', label: 'Disponible para Venta', icon: Package, color: 'bg-purple-500' },
    { key: 'entregado_kg', label: 'Entregado', icon: Truck, color: 'bg-cyan-500' },
    { key: 'pendiente_entrega_kg', label: 'Pendiente de Entrega', icon: Clock, color: 'bg-orange-500' },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Input oculto para subir archivos de contrato */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg"
        onChange={handleContratoFileSelected}
      />
      
      <div className="space-y-6" data-testid="inventario-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight">Inventario</h1>
            <p className="text-slate-600 mt-1">Control de stock, ventas y entregas de grano</p>
          </div>
          {currentUser.role === 'MASTER' && (
            <Button onClick={() => { resetCompraForm(); setIsCompraModalOpen(true); }} data-testid="add-compra-btn" className="bg-primary hover:bg-primary-hover text-white rounded-sm">
              <Plus className="mr-2" size={18} /> Registrar Compra
            </Button>
          )}
        </div>

        {/* Summary Cards - Totales Generales */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {summaryCards.map(card => {
            const Icon = card.icon;
            return (
              <div key={card.key} className="bg-white border border-slate-200 rounded-sm p-4 shadow-sm" data-testid={`summary-${card.key}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 font-medium">{card.label}</p>
                    <p className="text-xl font-bold text-slate-900 mt-1 font-mono">{formatKg(totales[card.key] || 0)}</p>
                  </div>
                  <div className={`${card.color} p-2 rounded-sm`}>
                    <Icon size={18} className="text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desglose por Grano */}
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Desglose por Tipo de Grano</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {resumen.map(item => {
              const colors = GRAIN_COLORS[item.tipo_grano] || GRAIN_COLORS.OTROS;
              return (
                <div key={item.tipo_grano} className={`${colors.bg} ${colors.border} border rounded-sm p-4`} data-testid={`grano-${item.tipo_grano}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`${colors.icon} p-1.5 rounded-sm`}>
                      <Wheat size={14} className="text-white" />
                    </div>
                    <h3 className={`font-bold ${colors.text}`}>{item.tipo_grano}</h3>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Comprado:</span>
                      <span className="font-mono font-semibold">{formatKg(item.comprado_kg)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Vendido:</span>
                      <span className="font-mono font-semibold text-emerald-700">{formatKg(item.vendido_kg)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Disponible:</span>
                      <span className={`font-mono font-bold ${item.disponible_venta_kg >= 0 ? 'text-purple-700' : 'text-red-600'}`}>
                        {formatKg(item.disponible_venta_kg)}
                      </span>
                    </div>
                    <div className="border-t border-slate-200 my-1"></div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Entregado:</span>
                      <span className="font-mono font-semibold text-cyan-700">{formatKg(item.entregado_kg)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Pend. Entrega:</span>
                      <span className={`font-mono font-bold ${item.pendiente_entrega_kg > 0 ? 'text-orange-600' : 'text-slate-500'}`}>
                        {formatKg(item.pendiente_entrega_kg)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            {resumen.length === 0 && (
              <div className="col-span-3 bg-white border border-slate-200 rounded-sm p-8 text-center">
                <Package size={48} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No hay datos de inventario. Registre compras o cree negocios para ver el resumen.</p>
              </div>
            )}
          </div>
        </div>

        {/* Tabla de Compras */}
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Registro de Compras</h2>
          {compras.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
              <ShoppingCart size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No hay compras registradas</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
              <table className="w-full" data-testid="compras-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Proveedor</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Grano</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Contrato</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase">Comprado</th>
                    <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500 uppercase">Cargado</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase">Pendiente</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Importadora</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase">USD/Ton</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase">Total USD</th>
                    <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500 uppercase">Acc.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {compras.map(c => {
                    const cantidadTon = (c.cantidad_kg || 0) / 1000;
                    const cargadoTon = (c.kg_cargados || 0) / 1000;
                    const pendienteTon = (c.kg_disponibles || 0) / 1000;
                    const ajusteTon = (c.ajuste_kg || 0) / 1000;
                    const pct = cantidadTon > 0 ? Math.min(100, (cargadoTon / cantidadTon) * 100) : 0;
                    const isCerrada = c.cerrada === true;
                    
                    return (
                      <tr key={c.id} className={`hover:bg-slate-50 transition-colors ${isCerrada ? 'bg-slate-50/50' : ''}`} data-testid={`compra-row-${c.id}`}>
                        <td className="px-3 py-2 text-sm font-mono">{formatDate(c.fecha_compra)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-slate-900">{c.proveedor_nombre}</span>
                            {isCerrada && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-100 text-emerald-700 rounded">
                                <CheckCircle size={10} />
                                CERRADA
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-sm ${
                            c.tipo_grano === 'MAIZ' ? 'bg-yellow-100 text-yellow-800' :
                            c.tipo_grano === 'TRIGO' ? 'bg-amber-100 text-amber-800' :
                            'bg-slate-100 text-slate-800'
                          }`}>{c.tipo_grano}</span>
                        </td>
                        <td className="px-3 py-2 text-sm font-mono">{c.contrato}</td>
                        <td className="px-3 py-2 text-sm font-mono text-right">{cantidadTon.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} T</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-col items-center gap-0.5">
                            <div className="flex items-center gap-1">
                              <span className={`text-sm font-mono font-semibold ${pct >= 100 || isCerrada ? 'text-emerald-600' : 'text-slate-700'}`}>
                                {cargadoTon.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} T
                              </span>
                              {ajusteTon !== 0 && (
                                <span className={`text-[10px] font-mono ${ajusteTon > 0 ? 'text-blue-600' : 'text-red-500'}`}>
                                  ({ajusteTon > 0 ? '+' : ''}{ajusteTon.toFixed(2)})
                                </span>
                              )}
                            </div>
                            <div className="w-16 h-1.5 bg-slate-200 rounded-full">
                              <div 
                                className={`h-1.5 rounded-full transition-all ${
                                  pct >= 100 || isCerrada ? 'bg-emerald-500' : 
                                  pct >= 60 ? 'bg-amber-400' : 
                                  pct > 0 ? 'bg-sky-400' : 'bg-slate-200'
                                }`} 
                                style={{ width: `${isCerrada ? 100 : pct}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-sm font-mono text-right">
                          <span className={`font-semibold ${pendienteTon <= 0 || isCerrada ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {pendienteTon.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} T
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-600">{c.importadora_nombre}</td>
                        <td className="px-3 py-2 text-sm font-mono text-right">${c.precio_unitario_usd?.toLocaleString('es-CL')}</td>
                        <td className="px-3 py-2 text-sm font-mono text-right font-semibold text-emerald-700">
                          ${((c.cantidad_kg / 1000) * c.precio_unitario_usd).toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {currentUser.role === 'MASTER' && (
                            <div className="flex items-center justify-center gap-0.5">
                              <Button 
                                onClick={() => openRentabilidadModal(c)} 
                                variant="ghost" 
                                size="sm" 
                                className={`h-7 w-7 p-0 ${c.rentabilidad_costo_aduanero_usd_ton !== null || c.rentabilidad_costo_admin_usd_ton !== null ? 'text-green-600 hover:bg-green-50' : 'text-slate-400 hover:bg-slate-50'}`}
                                title="Ver/Editar Rentabilidad"
                                data-testid={`rentabilidad-compra-${c.id}`}
                              >
                                <TrendingUp size={14} />
                              </Button>
                              <Button 
                                onClick={() => handleCerrarCompra(c)} 
                                variant="ghost" 
                                size="sm" 
                                className={`h-7 w-7 p-0 ${isCerrada ? 'text-amber-500 hover:bg-amber-50' : 'text-emerald-500 hover:bg-emerald-50'}`}
                                title={isCerrada ? 'Reabrir compra' : 'Cerrar compra (disponible = 0)'}
                                data-testid={`cerrar-compra-${c.id}`}
                              >
                                <CheckCircle size={14} />
                              </Button>
                              <Button onClick={() => openEditCompraModal(c)} variant="ghost" size="sm" className="text-blue-500 hover:bg-blue-50 h-7 w-7 p-0" data-testid={`edit-compra-${c.id}`}>
                                <Pencil size={14} />
                              </Button>
                              <Button onClick={() => handleDeleteCompra(c.id)} variant="ghost" size="sm" className="text-red-500 hover:bg-red-50 h-7 w-7 p-0" data-testid={`delete-compra-${c.id}`}>
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal Registrar Compra */}
        <Dialog open={isCompraModalOpen} onOpenChange={setIsCompraModalOpen}>
          <DialogContent className="max-w-lg" data-testid="compra-modal">
            <DialogHeader><DialogTitle>Registrar Compra de Grano</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateCompra} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Proveedor *</Label>
                  <Select value={compraForm.proveedor_id} onValueChange={v => setField('proveedor_id', v)}>
                    <SelectTrigger data-testid="compra-proveedor-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {proveedores.map(p => <SelectItem key={p.id} value={p.id}>{p.alias || p.razon_social}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tipo de Grano *</Label>
                  <Select value={compraForm.tipo_grano} onValueChange={v => setField('tipo_grano', v)}>
                    <SelectTrigger data-testid="compra-grano-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MAIZ">MAIZ</SelectItem>
                      <SelectItem value="TRIGO">TRIGO</SelectItem>
                      <SelectItem value="OTROS">OTROS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Contrato *</Label>
                  <Input value={compraForm.contrato} onChange={e => setField('contrato', e.target.value)} required className="uppercase" data-testid="compra-contrato-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Cantidad (Toneladas) *</Label>
                  <Input type="number" step="0.001" min="0" value={compraForm.cantidad_kg} onChange={e => setField('cantidad_kg', e.target.value)} required data-testid="compra-cantidad-input" placeholder="ej: 1000,00" /></div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Fecha de Compra *</Label>
                  <Input type="date" value={compraForm.fecha_compra} onChange={e => setField('fecha_compra', e.target.value)} required data-testid="compra-fecha-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Precio USD/Ton *</Label>
                  <Input type="number" step="0.01" value={compraForm.precio_unitario_usd} onChange={e => setField('precio_unitario_usd', e.target.value)} required data-testid="compra-precio-input" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Empresa Compradora (Importador) *</Label>
                  <Select value={compraForm.importadora_id} onValueChange={v => setField('importadora_id', v)}>
                    <SelectTrigger data-testid="compra-importadora-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {importadoras.map(i => <SelectItem key={i.id} value={i.id}>{i.alias || i.razon_social}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Observaciones</Label>
                  <Input value={compraForm.observaciones} onChange={e => setField('observaciones', e.target.value)} data-testid="compra-obs-input" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white" data-testid="submit-compra-btn">
                Registrar Compra
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        {/* Modal Editar Compra */}
        <Dialog open={isEditCompraModalOpen} onOpenChange={setIsEditCompraModalOpen}>
          <DialogContent className="max-w-md" data-testid="edit-compra-modal">
            <DialogHeader><DialogTitle>Editar Compra</DialogTitle></DialogHeader>
            <form onSubmit={handleEditCompra} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Cantidad (Toneladas) *</Label>
                  <Input type="number" step="0.001" min="0" value={editCompraForm.cantidad_kg}
                    onChange={e => setEditCompraForm(prev => ({ ...prev, cantidad_kg: e.target.value }))}
                    required data-testid="edit-compra-cantidad-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Fecha de Compra *</Label>
                  <Input type="date" value={editCompraForm.fecha_compra}
                    onChange={e => setEditCompraForm(prev => ({ ...prev, fecha_compra: e.target.value }))}
                    required data-testid="edit-compra-fecha-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Precio USD/Ton *</Label>
                  <Input type="number" step="0.01" value={editCompraForm.precio_unitario_usd}
                    onChange={e => setEditCompraForm(prev => ({ ...prev, precio_unitario_usd: e.target.value }))}
                    required data-testid="edit-compra-precio-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Observaciones</Label>
                  <Input value={editCompraForm.observaciones}
                    onChange={e => setEditCompraForm(prev => ({ ...prev, observaciones: e.target.value }))}
                    data-testid="edit-compra-obs-input" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white" data-testid="submit-edit-compra-btn">
                Actualizar Compra
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        
        {/* Modal Ajuste de Balanza */}
        <Dialog open={isAjusteModalOpen} onOpenChange={setIsAjusteModalOpen}>
          <DialogContent className="max-w-sm" data-testid="ajuste-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-blue-500" />
                Ajuste de Balanza
              </DialogTitle>
            </DialogHeader>
            {ajusteCompra && (
              <form onSubmit={handleGuardarAjuste} className="space-y-4">
                <div className="bg-slate-50 p-3 rounded-md text-sm space-y-1">
                  <div><strong>Proveedor:</strong> {proveedores.find(p => p.id === ajusteCompra.proveedor_id)?.nombre || '-'}</div>
                  <div><strong>Contrato:</strong> {ajusteCompra.contrato}</div>
                  <div><strong>Comprado:</strong> {formatKg(ajusteCompra.cantidad_kg)}</div>
                  <div><strong>Cargado:</strong> {formatKg(ajusteCompra.kg_cargados)}</div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Ajuste (toneladas)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={ajusteKg}
                    onChange={e => setAjusteKg(e.target.value)}
                    placeholder="Ej: -5.5 (merma) o +2.3 (ganancia)"
                    data-testid="ajuste-kg-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Positivo = más grano del registrado. Negativo = merma/pérdida.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setIsAjusteModalOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-ajuste-btn">
                    Guardar Ajuste
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Modal de Rentabilidad */}
        <Dialog open={isRentabilidadModalOpen} onOpenChange={setIsRentabilidadModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rentabilidad-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Rentabilidad de Compra
              </DialogTitle>
            </DialogHeader>
            
            {rentabilidadLoading ? (
              <div className="py-8 text-center text-slate-400">Cargando...</div>
            ) : rentabilidadData && (
              <div className="space-y-4">
                {/* Resumen de la compra */}
                <div className="bg-slate-50 p-3 rounded-md text-sm space-y-1">
                  <div className="flex justify-between">
                    <span><strong>Contrato:</strong> {rentabilidadData.contrato}</span>
                    <span><strong>Proveedor:</strong> {rentabilidadData.proveedor}</span>
                  </div>
                  <div className="flex justify-between">
                    <span><strong>Tipo:</strong> {rentabilidadData.tipo_grano}</span>
                    <span><strong>Tons Compradas:</strong> {rentabilidadData.cantidad_comprada_tons?.toLocaleString('es-CL')} T</span>
                  </div>
                  <div className="flex justify-between">
                    <span><strong>Tons Cargadas:</strong> {rentabilidadData.tons_cargadas?.toLocaleString('es-CL')} T</span>
                    <span><strong>Tons Descargadas:</strong> {rentabilidadData.tons_descargadas?.toLocaleString('es-CL')} T</span>
                  </div>
                  <div><strong>Camiones vinculados:</strong> {rentabilidadData.camiones_count}</div>
                </div>
                
                {/* Grid de ingresos y costos */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Columna Ingresos */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-emerald-700 uppercase border-b pb-1">Ingresos</h4>
                    <div className="bg-emerald-50 p-3 rounded-md">
                      <div className="text-xs text-emerald-600">Ingreso Total (automático)</div>
                      <div className="text-xl font-bold text-emerald-700">
                        $ {rentabilidadData.ingreso_total_usd?.toLocaleString('es-CL', { maximumFractionDigits: 0 })} USD
                      </div>
                      <div className="text-[10px] text-emerald-500 mt-1">
                        {rentabilidadData.tons_descargadas?.toLocaleString('es-CL')} tons × precio venta
                      </div>
                    </div>
                  </div>
                  
                  {/* Columna Costos Automáticos */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-red-700 uppercase border-b pb-1">Costos Automáticos</h4>
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Transporte:</span>
                        <span className="font-mono font-semibold text-red-600">
                          - $ {rentabilidadData.costo_transporte_usd?.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Proveedor Granos:</span>
                        <span className="font-mono font-semibold text-red-600">
                          - $ {rentabilidadData.costo_proveedor_usd?.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-400">
                        ({rentabilidadData.tons_cargadas?.toLocaleString('es-CL')} T × ${rentabilidadData.costo_proveedor_usd_ton}/T)
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Costos Manuales (formulario) */}
                <form onSubmit={handleGuardarRentabilidad} className="space-y-3 border-t pt-3">
                  <h4 className="text-xs font-semibold text-orange-700 uppercase">Costos Manuales</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px]">Costo Aduanero (USD/Ton)</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        value={rentabilidadForm.costo_aduanero_usd_ton}
                        onChange={e => setRentabilidadForm(prev => ({ ...prev, costo_aduanero_usd_ton: e.target.value }))}
                        placeholder="Ej: 11"
                        className="h-8 text-sm"
                        data-testid="rentabilidad-costo-aduanero"
                      />
                      <p className="text-[9px] text-slate-400">
                        Total: $ {((parseFloat(rentabilidadForm.costo_aduanero_usd_ton) || 0) * rentabilidadData.tons_cargadas).toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px]">Costo Admin (USD/Ton)</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        value={rentabilidadForm.costo_admin_usd_ton}
                        onChange={e => setRentabilidadForm(prev => ({ ...prev, costo_admin_usd_ton: e.target.value }))}
                        placeholder="Ej: 5"
                        className="h-8 text-sm"
                        data-testid="rentabilidad-costo-admin"
                      />
                      <p className="text-[9px] text-slate-400">
                        Total: $ {((parseFloat(rentabilidadForm.costo_admin_usd_ton) || 0) * rentabilidadData.tons_cargadas).toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px]">Otros Costos (USD Total)</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        value={rentabilidadForm.otros_costos_usd}
                        onChange={e => setRentabilidadForm(prev => ({ ...prev, otros_costos_usd: e.target.value }))}
                        placeholder="Ej: 500"
                        className="h-8 text-sm"
                        data-testid="rentabilidad-otros-costos"
                      />
                    </div>
                  </div>
                  
                  {/* Resumen de Utilidad */}
                  <div className={`p-4 rounded-lg ${rentabilidadData.utilidad_usd >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-[10px] text-slate-500 uppercase">Total Costos</div>
                        <div className="text-lg font-bold text-red-600">
                          $ {rentabilidadData.total_costos_usd?.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-500 uppercase">Utilidad</div>
                        <div className={`text-xl font-bold ${rentabilidadData.utilidad_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          $ {rentabilidadData.utilidad_usd?.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-500 uppercase">Margen / USD x Ton</div>
                        <div className={`text-lg font-bold ${rentabilidadData.margen_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {rentabilidadData.margen_pct?.toFixed(1)}% / ${rentabilidadData.utilidad_por_ton_usd?.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setIsRentabilidadModalOpen(false)}>
                      Cerrar
                    </Button>
                    <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="submit-rentabilidad-btn">
                      Guardar Costos
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Inventario;
